import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import BasicModal from 'components/elements/BasicModal';
import { Loading } from 'components/elements/Loading';
import CreateGroupView from 'components/widgets/CompanyDetails/SaveCompanyToGroupModal/CreateGroupView';
import GroupSelectionView from 'components/widgets/CompanyDetails/SaveCompanyToGroupModal/GroupSelectionView';
import {
  useGroupConfigurations,
  useInvalidateGroupConfigurations,
  useSaveCompanyToGroupConfig,
} from 'hooks/useGroupConfig';
import { useToast } from 'hooks/useToast';
import { useAvailableCities } from 'hooks/useUserCityAccess';
import { trackGroupConfigAction } from 'services/analytics/trackGroupConfigAction';
import GroupConfigurationAPI from 'services/API/GroupConfigurationAPI';
import { useUser } from 'state/auth';
import { DEFAULT_GROUP } from 'state/dashboard';
import { trackSaveCompany } from 'utils/analytics';
import {
  CompanyDetailsPageViewSections,
  CompanyDetailsPageViewSubSections,
  ExportProjectListGroupTypes,
} from 'utils/analytics/constants';
import { GROUP_TYPE, NEW_GROUP_ID } from 'utils/constants';
import { State } from 'utils/constants/location';
import { groupToGroupRaw } from 'utils/transforms';
import type { Group, GroupRaw } from 'types/misc';

function buildNewCompanyGroupPayload({
  name,
  stateCode,
  regionIds,
  companyId,
}: {
  name: string;
  stateCode: State;
  regionIds: string[];
  companyId?: number;
}): GroupRaw {
  return groupToGroupRaw({
    ...DEFAULT_GROUP,
    id: NEW_GROUP_ID,
    name,
    type: GROUP_TYPE.COMPANY,
    state_code: stateCode,
    mercator_metropolitan_ids: regionIds as Group['mercator_metropolitan_ids'],
    mercatorMetropolitanId: (regionIds[0] as Group['mercatorMetropolitanId']) ?? null,
    companies: companyId != null ? [companyId] : [],
  });
}

type SaveCompanyToGroupModalSuccessResult = {
  groupId: number;
  groupName: string;
};

type Props = {
  onClose(): void;
  onSuccess(result?: SaveCompanyToGroupModalSuccessResult): void;
  isOpen: boolean;
  companyId?: number;
  /** When true, only show CreateGroupView (no group list). Used when creating a new group from dashboard. */
  createOnly?: boolean;
};

type ViewMode = 'list' | 'create';

const SaveCompanyToGroupModal = ({
  onClose,
  onSuccess,
  isOpen,
  companyId,
  createOnly = false,
}: Props) => {
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>(createOnly ? 'create' : 'list');
  const [newGroupState, setNewGroupState] = useState<State>(State.TX);
  const [newGroupName, setNewGroupName] = useState('');
  const { renderToast } = useToast();
  const { user } = useUser();
  const { availableStates } = useAvailableCities();
  const {
    data: userGroupsData,
    isLoading: isLoadingGroups,
    refetch: refetchGroups,
  } = useGroupConfigurations({
    userId: String(user?.id),
    groupType: GROUP_TYPE.COMPANY,
  });
  const { addCompanyToGroupConfigs } = useSaveCompanyToGroupConfig();
  const { invalidateGroupConfigurations } = useInvalidateGroupConfigurations();

  const createStateOptions = useMemo(() => {
    return (Object.keys(availableStates) as State[]).filter(
      (state) =>
        availableStates[state] && Object.keys(availableStates[state].regions).length > 0,
    );
  }, [availableStates]);

  const regionIdsForSelectedState = useMemo(() => {
    if (!newGroupState || !availableStates[newGroupState]) return [];

    return Object.keys(availableStates[newGroupState].regions);
  }, [availableStates, newGroupState]);

  useEffect(() => {
    if (addCompanyToGroupConfigs.isSuccess) {
      const response = addCompanyToGroupConfigs.data;

      if (response.added_to && response.added_to.length) {
        renderToast({
          toastTitle: 'saved_company_to_group_config',
          title: 'Company was saved in group(s):',
          label: `${response.added_to.join(', ')}`,
        });
        if (onSuccess) {
          onSuccess();
        }
      }
      if (response.already_in && response.already_in.length) {
        renderToast({
          toastTitle: 'saved_company_to_group_config',
          title: 'This company is already saved in group(s):',
          label: `${response.already_in.join(', ')}`,
        });
        onClose();
      }
    }
    if (addCompanyToGroupConfigs.isError) {
      renderToast({
        toastTitle: 'error_saving_company',
        title: 'Error',
        label: 'Something went wront while saving this company.',
        status: 'error',
      });
    }
  }, [addCompanyToGroupConfigs.isSuccess, addCompanyToGroupConfigs.isError]);

  const handleConfirm = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (selectedItems && companyId) {
      addCompanyToGroupConfigs.mutate({
        groupIds: selectedItems,
        companyId,
      });
      const input = event.target as HTMLElement;
      const buttonText = input.innerText;
      trackSaveCompany({
        companyId,
        groupIds: selectedItems,
        groupType: ExportProjectListGroupTypes.COMPANY_GROUP,
        clickText: buttonText,
        sectionName: CompanyDetailsPageViewSections.COMPANY_DETAILS,
        subSectionName: CompanyDetailsPageViewSubSections.COMPANY_INFORMATION,
      });
    }
    setSelectedItems([]);
  };

  const selectedListsLabel = useMemo(() => {
    return selectedItems.length === 1
      ? '1 group selected'
      : `${selectedItems.length} groups selected`;
  }, [selectedItems]);

  const createGroupMutation = useMutation({
    mutationFn: async ({
      name,
      stateCode,
      regionIds,
      companyId: cid,
    }: {
      name: string;
      stateCode: State;
      regionIds: string[];
      companyId?: number;
    }) => {
      const group = buildNewCompanyGroupPayload({
        name: name.trim(),
        stateCode,
        regionIds,
        companyId: cid,
      });
      const payload = group;
      const response = await GroupConfigurationAPI.postCreateNewSavedSearch(payload, {
        timeout: 30000,
      });

      return response?.data?.data?.group_config_id as number;
    },
    onSuccess: (newGroupId, variables) => {
      trackGroupConfigAction({
        group_id: String(newGroupId),
        group_type: 'company_group',
        click_action: 'create_group',
      });
      if (variables.companyId) {
        trackSaveCompany({
          companyId: variables.companyId,
          groupIds: [newGroupId],
          groupType: ExportProjectListGroupTypes.COMPANY_GROUP,
          clickText: 'Save & Continue',
          sectionName: CompanyDetailsPageViewSections.COMPANY_DETAILS,
          subSectionName: CompanyDetailsPageViewSubSections.COMPANY_INFORMATION,
        });
      }
      invalidateGroupConfigurations(String(user?.id), GROUP_TYPE.COMPANY);
      refetchGroups();
      setViewMode('list');
      setNewGroupName('');
      setNewGroupState(
        createStateOptions.includes(State.TX)
          ? State.TX
          : (createStateOptions[0] ?? State.TX),
      );
      if (variables.companyId) {
        renderToast({
          toastTitle: 'saved_company_to_group_config',
          title: 'Company was saved in group(s):',
          label: variables.name?.trim() ?? '',
        });
      }
      if (onSuccess) {
        onSuccess({
          groupId: newGroupId,
          groupName: variables.name?.trim() ?? '',
        });
      }
    },
    onError: () => {
      renderToast({
        toastTitle: 'error_saving_company',
        title: 'Error',
        label: 'Something went wrong while creating the group.',
        status: 'error',
      });
    },
  });

  const handleCreateNewGroup = () => {
    setViewMode('create');
    setNewGroupName('');
    setNewGroupState(
      createStateOptions.includes(State.TX)
        ? State.TX
        : (createStateOptions[0] ?? State.TX),
    );
    trackGroupConfigAction({
      group_id: '0',
      group_type: 'company_group',
      click_action: 'start_group',
    });
  };

  const handleSaveAndContinue = useCallback(() => {
    const name = newGroupName.trim();
    if (!name || regionIdsForSelectedState.length === 0) return;
    createGroupMutation.mutate({
      name,
      stateCode: newGroupState,
      regionIds: regionIdsForSelectedState,
      companyId,
    });
  }, [newGroupName, newGroupState, regionIdsForSelectedState, companyId]);

  const toggleGroupSelection = (groupId: number) => {
    setSelectedItems((prev) =>
      prev.includes(groupId) ? prev.filter((i) => i !== groupId) : [...prev, groupId],
    );
  };

  const hasCompanyGroups = useMemo(
    () => userGroupsData != null && userGroupsData.length > 0,
    [userGroupsData],
  );

  useEffect(() => {
    if (!isOpen) {
      setViewMode(createOnly ? 'create' : 'list');
    } else if (createOnly) {
      setViewMode('create');
    }
  }, [isOpen, createOnly]);

  const isCreating = viewMode === 'create' || createOnly;
  const displayGroups = useMemo(() => {
    if (!userGroupsData?.length) return userGroupsData ?? [];

    return [...userGroupsData].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
  }, [userGroupsData]);

  return (
    <BasicModal isOpen={isOpen} onClose={onClose}>
      <Loading>
        {isCreating ? (
          <CreateGroupView
            groupName={newGroupName}
            onGroupNameChange={setNewGroupName}
            selectedState={newGroupState}
            onStateChange={setNewGroupState}
            stateOptions={createStateOptions}
            regionIdsForSelectedState={regionIdsForSelectedState}
            isLoading={createGroupMutation.isLoading}
            onBack={createOnly ? onClose : () => setViewMode('list')}
            onSaveAndContinue={handleSaveAndContinue}
          />
        ) : (
          <GroupSelectionView
            displayGroups={displayGroups}
            isLoading={isLoadingGroups}
            hasCompanyGroups={hasCompanyGroups}
            selectedItems={selectedItems}
            selectedListsLabel={selectedListsLabel}
            onToggleGroup={toggleGroupSelection}
            onCreateNewGroup={handleCreateNewGroup}
            onClose={onClose}
            onConfirm={handleConfirm}
            isConfirmLoading={addCompanyToGroupConfigs.isLoading}
            isConfirmDisabled={
              !selectedItems?.length ||
              addCompanyToGroupConfigs.isLoading ||
              !hasCompanyGroups
            }
          />
        )}
      </Loading>
    </BasicModal>
  );
};

export default SaveCompanyToGroupModal;
